const PRINT_WINDOW_FEATURES = 'noopener,noreferrer,width=1280,height=900';
const PRINT_PREP_DELAY_MS = 300;
const IMAGE_LOAD_TIMEOUT_MS = 1200;

const waitForImage = (image: HTMLImageElement) => new Promise<void>((resolve) => {
  if (image.complete) {
    resolve();
    return;
  }

  const finish = () => {
    image.removeEventListener('load', finish);
    image.removeEventListener('error', finish);
    resolve();
  };

  image.addEventListener('load', finish, { once: true });
  image.addEventListener('error', finish, { once: true });
  window.setTimeout(finish, IMAGE_LOAD_TIMEOUT_MS);
});

const buildHeadMarkup = (title: string) => {
  const styleNodes = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((node) => node.outerHTML)
    .join('\n');

  return `
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    <base href="${window.location.origin}/" />
    ${styleNodes}
    <style>
      @page {
        margin: 12mm;
      }

      html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
      }

      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .lt-topbar,
      .pro-topbar,
      aside,
      .lt-close,
      .lt-pill,
      .lt-cta,
      .pro-btn,
      .lt-overlay-close {
        display: none !important;
      }

      .lt-print-root,
      .pro-print-root {
        position: static !important;
        inset: auto !important;
        z-index: auto !important;
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow: visible !important;
        background: #ffffff !important;
      }

      .lt-print-root .lt-scroll,
      .pro-print-root .pro-main,
      .pro-print-root .pro-shell {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }

      .pro-print-root .pro-shell {
        display: block !important;
      }

      .lt-print-root .lt-sec,
      .pro-print-root section {
        min-height: auto !important;
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .lt-print-root .lt-inner {
        max-width: none !important;
      }

      .lt-print-root .lt-step,
      .pro-print-root .pro-reveal {
        opacity: 1 !important;
        transform: none !important;
      }

      .lt-print-root .lt-badge,
      .lt-print-root .lt-avatar-chip,
      .lt-print-root .lt-confetti-dot,
      .lt-print-root .lt-float,
      .lt-print-root .lt-pulse,
      .lt-print-root .lt-spin {
        animation: none !important;
      }
    </style>
  `;
};

export const printTakeawayNode = async (
  node: HTMLElement | null,
  title: string,
  rootClassName: string,
) => {
  if (!node) {
    window.print();
    return;
  }

  const printWindow = window.open('', '_blank', PRINT_WINDOW_FEATURES);
  if (!printWindow) {
    window.print();
    return;
  }

  const clonedNode = node.cloneNode(true) as HTMLElement;
  clonedNode.classList.add(rootClassName);

  printWindow.document.open();
  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        ${buildHeadMarkup(title)}
      </head>
      <body>
        ${clonedNode.outerHTML}
      </body>
    </html>
  `);
  printWindow.document.close();

  const images = Array.from(printWindow.document.images);
  await Promise.all(images.map(waitForImage));

  window.setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, PRINT_PREP_DELAY_MS);
};
